/**
 * Trade landing pages — single source of truth.
 *
 * Each entry drives one public, indexable page (pages/TradePage.jsx): its
 * route, its SEO metadata (seo.js reads TRADES), its visible copy, and its
 * FAQPage JSON-LD. The sample invoice amounts are computed from `lines` at
 * render time — never hand-typed totals — so the example math is always
 * right.
 *
 * When adding a trade, also add its path to scripts/prerender.mjs ROUTES,
 * public/sitemap.xml and every user-agent block in public/robots.txt
 * (trades.test.js fails if any is missed).
 *
 * The `faqs` answers are public claims about app behavior (and ship as
 * FAQPage JSON-LD) — keep them in step with data/faq.js when behavior
 * changes.
 *
 * Copy rules: only claim what the app actually does today (see faq.js), and
 * keep tax/legal guidance general — rules vary by state.
 */
export const TRADES = [
  {
    slug: "plumbers",
    invoiceNoun: "a plumbing invoice",
    name: "Plumbers",
    title: "Invoice app for plumbers — speak it, send a PDF | HandInvoice",
    description:
      "Make a plumbing invoice by talking into your phone. Labor, parts, haul-away fees and tax become a professional PDF in about a minute. Free, no signup.",
    keywords:
      "plumber invoice, plumbing invoice app, plumbing invoice template, invoice app for plumbers, plumber invoice by voice",
    headline: "Plumbing invoices, done before you're back in the truck.",
    intro:
      "You just swapped a water heater or cleared a main line. The last thing you want is to type parts into a form on a tiny screen with wet hands. With HandInvoice you tap record, say what you did the way you'd tell the homeowner, and get a clean PDF invoice you can text or email before you leave the driveway.",
    say: "Replaced the water heater for Dana Price at 418 Elm Street. Flat 650 for the install, the 50-gallon heater was 720 from Ferguson, plus 40 to haul away the old one. Eight percent tax.",
    customer: "Dana Price · 418 Elm Street",
    lines: [
      { description: "Water heater install (flat rate)", qty: 1, rate: 650 },
      { description: "50-gallon gas water heater", qty: 1, rate: 720 },
      { description: "Haul-away of old unit", qty: 1, rate: 40 },
    ],
    taxPct: 8,
    include: [
      "Your business name, phone and plumbing license number (set once in Settings).",
      "The job address, which matters when the person paying is a landlord or property manager.",
      "Labor as hours × rate or a flat rate, whichever you quoted.",
      "Each part as its own line (fixtures, valves, the heater itself) so the customer sees where the money went.",
      "Disposal, haul-away or permit fees as separate lines.",
      "Payment terms (due on receipt, or due in 15 days) and how you take payment.",
    ],
    faqs: [
      {
        q: "Can I bill a flat rate instead of hourly?",
        a: 'Yes. Say "flat 650 for the install" and it becomes one flat-rate line. Say "3 hours at 95" and it becomes hours × rate. You can mix both on one invoice.',
      },
      {
        q: "Will it get my parts prices right?",
        a: "It writes down the numbers you say, and it never guesses one. If a price isn't clear, the line is flagged on the Review screen so you can fill it in. You check every line before the PDF is made.",
      },
      {
        q: "Can I add my license number to the invoice?",
        a: "Yes. Add it once in Settings along with your business name, phone and logo, and it prints on every invoice.",
      },
    ],
  },
  {
    slug: "electricians",
    invoiceNoun: "an electrical invoice",
    name: "Electricians",
    title: "Invoice app for electricians — invoice by voice | HandInvoice",
    description:
      "Electrician invoices without the paperwork. Say your hours, panel and breaker parts, and permit fees, and get a professional PDF invoice. Free, works on any phone.",
    keywords:
      "electrician invoice, electrical invoice template, invoice app for electricians, electrical contractor invoice, electrician invoice by voice",
    headline: "Electrical invoices without the clipboard.",
    intro:
      "Panel upgrades, new circuits and service calls all come with a pile of parts and a permit to bill for. HandInvoice lets you talk through the job (hours, breakers, wire, permit fee) and turns it into an itemized PDF invoice with your license number on it. Nothing to install and no account needed.",
    say: "Panel upgrade for Mike Torres. Six hours at 95, the 200-amp panel was 340, twelve breakers at 9 each, and 150 for the permit.",
    customer: "Mike Torres",
    lines: [
      { description: "Labor: panel upgrade", qty: 6, unit: "hr", rate: 95 },
      { description: "200A main breaker panel", qty: 1, rate: 340 },
      { description: "Circuit breakers", qty: 12, rate: 9 },
      { description: "Electrical permit fee", qty: 1, rate: 150 },
    ],
    taxPct: null,
    include: [
      "Your electrical license number, which customers and inspectors often look for.",
      "Labor hours and your rate, or the flat price you quoted.",
      "Major components (panel, breakers, fixtures, wire) as separate lines with quantities.",
      "Permit and inspection fees on their own lines.",
      "Tax, if it applies to your work in your state. HandInvoice adds it when you say it.",
      "When payment is due, and whether you've already taken a deposit.",
    ],
    faqs: [
      {
        q: 'Can I say quantities like "twelve breakers at 9 each"?',
        a: "Yes. Quantities and unit prices become a quantity × rate line, and the amount is calculated for you, so 12 × $9 shows as $108.",
      },
      {
        q: "Can I record a deposit the customer already paid?",
        a: 'Yes. Say something like "they paid 200 up front by check" and the invoice shows the deposit and the remaining balance due.',
      },
      {
        q: "What if I don't charge tax?",
        a: 'Say "no tax" and none is added. If you usually charge tax, set a default in Settings and it fills in whenever you don\'t mention tax. Either way you can turn it on or off on the Review screen before the PDF is made.',
      },
    ],
  },
  {
    slug: "handyman",
    invoiceNoun: "a handyman invoice",
    name: "Handyman",
    title: "Handyman invoice app — free invoices by voice | HandInvoice",
    description:
      "A handyman invoice for a day of odd jobs, made by talking. Hours, materials and repeat-customer discounts become a clean PDF invoice in a minute. Free, no signup.",
    keywords:
      "handyman invoice, handyman invoice template, handyman invoice app, free handyman invoice, invoice app for handyman",
    headline: "A day of odd jobs, one invoice, one minute.",
    intro:
      "Handyman work is a bunch of little jobs on one visit: hang a door, patch the hallway, fix a running toilet. Itemizing all of that is the part nobody enjoys. With HandInvoice you list it out loud as you walk to the truck, and it builds the line items, adds the materials, applies your discount and gives you a PDF ready to send.",
    say: "Odd jobs for Linda Park. Hung two doors and patched the hallway drywall, four hours at 65. Drywall and mud were 28, door hardware 46. Take 20 off, she's a repeat customer.",
    customer: "Linda Park",
    lines: [
      { description: "Labor: hang 2 doors, patch drywall", qty: 4, unit: "hr", rate: 65 },
      { description: "Drywall & joint compound", qty: 1, rate: 28 },
      { description: "Door hardware", qty: 1, rate: 46 },
    ],
    discount: 20,
    taxPct: null,
    include: [
      "A short description of each task, so the customer remembers what they're paying for.",
      "Your hours and rate, or a flat price per task.",
      "Materials you picked up, with the total for each.",
      "Any discount for repeat or referral customers, shown clearly in the totals.",
      "Your phone number and how you take payment (cash, check, Venmo, Zelle).",
      "A short thank-you or note, which you can set once and reuse.",
    ],
    faqs: [
      {
        q: "Can I give a discount?",
        a: 'Yes. Say something like "take 20 off for a repeat customer", and the discount appears in the totals, below the subtotal. You can also add or change it on the Review screen.',
      },
      {
        q: "Do I need an account?",
        a: "No. You can record, review and download the PDF without signing up. Sign in only if you want your past invoices saved.",
      },
      {
        q: "Can I put my payment methods on the invoice?",
        a: "Yes. Add them once in Settings (for example \"Cash, check, Venmo @yourname\") and they print in a PAYMENT section on every invoice.",
      },
    ],
  },
  {
    slug: "hvac",
    invoiceNoun: "an HVAC invoice",
    name: "HVAC",
    title: "HVAC invoice app — invoice service calls by voice | HandInvoice",
    description:
      "HVAC invoices from a voice note. Service call fee, labor, parts and refrigerant become a professional PDF invoice before you leave the job. Free to use.",
    keywords:
      "HVAC invoice, HVAC invoice template, HVAC service invoice, invoice app for HVAC, HVAC technician invoice",
    headline: "Close out the service call by talking.",
    intro:
      "A tune-up turns into a capacitor swap and a refrigerant top-off, and now there's a service fee, labor, parts and pounds of refrigerant to bill. With HandInvoice you describe the call out loud and it lays out each charge on its own line, adds tax and gives you a PDF the customer can pay from.",
    say: "AC tune-up for the Nguyens. 89 service call, an hour and a half at 110, capacitor was 45, and two pounds of refrigerant at 60. Seven percent tax.",
    customer: "The Nguyen family",
    lines: [
      { description: "Service call", qty: 1, rate: 89 },
      { description: "Labor: AC tune-up & repair", qty: 1.5, unit: "hr", rate: 110 },
      { description: "Run capacitor", qty: 1, rate: 45 },
      { description: "Refrigerant", qty: 2, unit: "lb", rate: 60 },
    ],
    taxPct: 7,
    include: [
      "The service call or trip fee as its own line.",
      "Labor time, which may include fractions like 1.5 hours.",
      "Each part replaced (capacitors, contactors, filters, motors).",
      "Refrigerant by the pound, with the quantity shown.",
      "Tax where it applies, and your payment terms.",
      "Your license number and company details at the top.",
    ],
    faqs: [
      {
        q: 'Does it handle partial hours like "an hour and a half"?',
        a: "Yes. Spoken amounts like an hour and a half become 1.5 hours, multiplied by your rate. You can adjust any number on the Review screen.",
      },
      {
        q: "Can I bill refrigerant by the pound?",
        a: 'Yes. Say "two pounds of refrigerant at 60" and it becomes a quantity × rate line.',
      },
      {
        q: "Can I set due dates?",
        a: "Yes. Set default payment terms in Settings (due on receipt, due in 15 days, and so on), and every invoice shows the due date. You can change it on any single invoice.",
      },
    ],
  },
];

export const TRADE_PATHS = TRADES.map((t) => "/" + t.slug);

/** Line amount, rounded to cents. */
export function lineAmount(line) {
  return Math.round(line.qty * line.rate * 100) / 100;
}

/** Totals for a trade's sample invoice, computed from its lines. */
export function sampleTotals(trade) {
  const gross =
    Math.round(trade.lines.reduce((s, l) => s + lineAmount(l), 0) * 100) / 100;
  const discount = trade.discount || 0;
  const net = gross - discount;
  const tax = trade.taxPct ? Math.round(net * trade.taxPct) / 100 : 0;
  return { gross, discount, tax, total: Math.round((net + tax) * 100) / 100 };
}
